import { CoreEventBus } from '@databases/core-event-bus';
import { container, type ContainerOverrideOptions } from '@databases/service-container';
import { DatabaseFieldType } from '@databases/types/database';
import aiSummaryFieldType from '@databases/utils/database/field-types/aiSummary';
import attachmentFieldType from '@databases/utils/database/field-types/attachment';
import backlinkFieldType from '@databases/utils/database/field-types/backlink';
import checkboxFieldType from '@databases/utils/database/field-types/checkbox';
import dateFieldType from '@databases/utils/database/field-types/date';
import entryDetailsFieldType from '@databases/utils/database/field-types/entryDetails';
import entryLinkFieldType from '@databases/utils/database/field-types/entryLink';
import hyperlinkFieldType from '@databases/utils/database/field-types/hyperlink';
import jiraIssueFieldType from '@databases/utils/database/field-types/jiraIssue';
import jiraIssueDetailsFieldType from '@databases/utils/database/field-types/jiraIssueDetails';
import numberFieldType from '@databases/utils/database/field-types/number';
import pageDetailsFieldType from '@databases/utils/database/field-types/pageDetails';
import pageLabelsFieldType from '@databases/utils/database/field-types/pageLabels';
import pageLinkFieldType from '@databases/utils/database/field-types/pageLink';
import pageStatusFieldType from '@databases/utils/database/field-types/pageStatus';
import selectFieldType from '@databases/utils/database/field-types/select';
import textFieldType from '@databases/utils/database/field-types/text';
import userFieldType from '@databases/utils/database/field-types/user';

/**
 * Registers the core dependencies that are needed before the rest of the container dependencies.
 *
 * Returns a function to register additional dependencies.
 */
export function registerDependenciesRequiredBeforeBootstrap() {
  /**
   * EventBus is used by the bridge data, which is required before bootstrap.
   */
  container.registerSingleton('EventBus', () => new CoreEventBus());

  /**
   * Registers service container dependencies.
   *
   * Note: SelectionsActions is excluded as it is registered separately via
   * initializeRecyclableSelectionsMachine after bootstrap completes.
   */
  return function registerOrOverrideServiceContainerDependencies(
    overrides: ContainerOverrideOptions
  ) {
    container.registerSingleton('FlagsManager', overrides.FlagsManager);
    container.registerSingleton('PluginsManager', overrides.PluginsManager);
    container.registerSingleton('PanelManager', overrides.PanelManager);
    container.registerSingleton('PreviewPanelAFMBridge', overrides.PreviewPanelAFMBridge);

    /**
     * This needs to be a factory so that whenever this is read, the latest locale is referenced.
     */
    container.registerFactory('Intl', overrides.Intl);

    container.registerSingleton('FieldTypeRegistry', () => ({
      [DatabaseFieldType.TEXT]: textFieldType,
      [DatabaseFieldType.AI_SUMMARY]: aiSummaryFieldType,
      [DatabaseFieldType.NUMBER]: numberFieldType,
      [DatabaseFieldType.CHECKBOX]: checkboxFieldType,
      [DatabaseFieldType.PAGE_LINK]: pageLinkFieldType,
      [DatabaseFieldType.ATTACHMENT]: attachmentFieldType,
      [DatabaseFieldType.USER]: userFieldType,
      [DatabaseFieldType.DATE]: dateFieldType,
      [DatabaseFieldType.SELECT]: selectFieldType,
      [DatabaseFieldType.HYPERLINK]: hyperlinkFieldType,
      [DatabaseFieldType.JIRA_ISSUE]: jiraIssueFieldType,
      [DatabaseFieldType.ENTRY_LINK]: entryLinkFieldType,
      [DatabaseFieldType.ENTRY_DETAILS]: entryDetailsFieldType,
      [DatabaseFieldType.BACKLINK]: backlinkFieldType,
      [DatabaseFieldType.PAGE_LABELS]: pageLabelsFieldType,
      [DatabaseFieldType.PAGE_STATUS]: pageStatusFieldType,
      [DatabaseFieldType.PAGE_DETAILS]: pageDetailsFieldType,
      [DatabaseFieldType.JIRA_ISSUE_DETAILS]: jiraIssueDetailsFieldType,
    }));
  };
}
